import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";
import { NAV_HEIGHT } from "@/lib/navbar";

const CSS = `
  .tg-page1 {
    background: #F5F5F7;
    height: 100vh;
    height: 100dvh;
    min-height: 640px;
    /* Épinglé en haut : le hero se fige pendant que la section "Le Mouvement"
       (z-index supérieur) grimpe par-dessus lui — effet "montée sur le hero"
       facon Apple. Il se dé-épingle à la fin du wrapper hero+mouvement. */
    position: sticky;
    top: 0;
    z-index: 1;
    overflow: hidden;
    margin-top: -${NAV_HEIGHT}px; /* passe sous la navbar fixe/transparente, pleine bavure */
  }

  /* Visuel détouré, ancré en haut (juste sous la navbar), centré sur le fond clair */
  .tg-p1-visual {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1;
    overflow: hidden;
  }

  .tg-p1-visual img {
    height: 104%;
    width: auto;
    max-width: none;
    object-fit: contain;
    /* Sujet poussé vers la droite (comme Apple Vision) : on dégage l'espace
       blanc à gauche pour le texte. Un object-position-x plus bas décale la
       fenêtre visible vers le haut de gamme, donc la femme glisse à droite. */
    object-position: 34% top;
    display: block;
    /* Pas de filtre : brightness(0.86) assombrissait le fond de l'image (~#DADADA)
       alors que le conteneur reste en #F5F5F7 → un liseré clair apparaissait sur
       les cotes (l'image 16:9 ne remplit pas toute la largeur). Sans filtre, le
       fond image (#F6F5F8) = fond conteneur (#F5F5F7) → raccord invisible. */
    filter: none;
  }

  /* Voile clair en haut : protège la lisibilité des liens de la navbar
     (transparente, sans fond propre) quelle que soit la zone de l'image
     qui se trouve dessous — évite qu'une zone sombre de la photo (ex.
     des cheveux) ne vienne "manger" le texte sombre de la navbar. */
  .tg-p1-topscrim {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 130px;
    background: linear-gradient(to bottom, rgba(245,245,247,0.6) 0%, rgba(245,245,247,0.28) 55%, rgba(245,245,247,0) 100%);
    z-index: 4;
    pointer-events: none;
  }

  /* Voile clair qui remonte depuis le bas : le texte passe sur l'image, lisible
     quelle que soit la couleur sous-jacente. Fondu progressif dès la base
     (pas de palier quasi-opaque avant de dégrader) pour une dissolution douce
     façon Apple Vision, plutôt qu'un bandeau clair qui tranche sur l'image. */
  .tg-p1-scrim {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 64%;
    background: linear-gradient(to top,
      rgba(245,245,247,1) 0%,
      rgba(245,245,247,0.86) 10%,
      rgba(245,245,247,0.68) 20%,
      rgba(245,245,247,0.50) 30%,
      rgba(245,245,247,0.36) 40%,
      rgba(245,245,247,0.25) 50%,
      rgba(245,245,247,0.16) 60%,
      rgba(245,245,247,0.09) 70%,
      rgba(245,245,247,0.05) 80%,
      rgba(245,245,247,0.02) 90%,
      rgba(245,245,247,0) 100%);
    z-index: 5;
    pointer-events: none;
  }

  /* ── Overlay texte (gauche), posé bas sur le voile ── */
  .tg-p1-overlay {
    position: absolute;
    left: 0;
    bottom: 8%;
    width: 56%;
    z-index: 10;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-left: 72px;
    padding-right: 24px;
  }

  .tg-p1-title {
    margin: 0 0 44px;
    font-size: clamp(32px, 4.2vw, 58px);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.08;
    color: #1D1D1F;
    font-family: ${FONT};
  }

  .tg-p1-title-accent {
    color: #1D1D1F;
  }

  .tg-p1-sub {
    font-size: 17px;
    font-weight: 700;
    color: #123D34;
    font-family: ${FONT};
    letter-spacing: -0.01em;
    line-height: 1.5;
    margin: 0;
    max-width: 620px;
    white-space: nowrap;
  }

  /* ── CTAs ancrés en bas à droite du hero ── */
  .tg-p1-links {
    position: absolute;
    right: 5%;
    bottom: 8%;
    z-index: 10;
    display: flex;
    flex-direction: row;
    gap: 14px;
    align-items: center;
  }

  .tg-p1-btn-black {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 50px;
    padding: 0 24px;
    border-radius: 999px;
    background: #0A0A0A;
    color: #FFFFFF;
    font-family: ${FONT};
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: all 0.2s ease;
  }
  .tg-p1-btn-black:hover { background: #1E1E1E; transform: scale(1.02); }

  .tg-p1-btn-ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    padding: 0 24px;
    border-radius: 999px;
    background: transparent;
    color: #1D1D1F;
    font-family: ${FONT};
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    border: 1px solid #123D34;
    transition: all 0.2s ease;
  }
  .tg-p1-btn-ghost:hover { background: rgba(0,0,0,0.04); transform: scale(1.02); }

  /* Responsive */
  @media (max-width: 1024px) {
    .tg-p1-overlay { width: 62%; padding-left: 40px; }
    .tg-p1-sub { white-space: normal; max-width: 460px; }
  }

  @media (max-width: 768px) {
    .tg-page1 { height: 100svh; min-height: 600px; }
    /* Photo paysage avec sujet centré + grandes marges blanches : en portrait
       mobile, on recadre sur elle (cover) au lieu de montrer une tranche vide. */
    .tg-p1-visual img {
      height: 100%;
      width: 100%;
      object-fit: cover;
      object-position: 52% 14%;
    }
    /* Apple Vision mobile : titre noir centré, compact, posé sur la zone du
       voile la plus dense (bas) = lisibilité garantie. */
    .tg-p1-overlay {
      width: 100%;
      left: 0;
      right: 0;
      bottom: 20%;
      padding-left: 24px;
      padding-right: 24px;
      align-items: center;
      text-align: center;
    }
    .tg-p1-title {
      font-size: clamp(26px, 7vw, 34px);
      line-height: 1.1;
      margin-bottom: 12px;
    }
    .tg-p1-sub {
      font-size: 14px;
      max-width: 320px;
      margin: 0 auto;
    }
    /* 2 CTA côte à côte, centrés, plus petits. */
    .tg-p1-links {
      left: 0;
      right: 0;
      bottom: 7%;
      gap: 10px;
      padding: 0 24px;
      justify-content: center;
      flex-wrap: nowrap;
    }
    .tg-p1-btn-black, .tg-p1-btn-ghost {
      height: 44px;
      padding: 0 18px;
      font-size: 13px;
      flex: 0 1 auto;
      min-width: 0;
    }
  }
`;

export default function HeroSection() {
  return (
    <>
      <style>{CSS}</style>
      <div className="tg-page1" data-navbar-theme="light">

        {/* Visuel détouré, centré */}
        <motion.div
          className="tg-p1-visual"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: appleEase }}
        >
          <img
            src="/images/hero-tangig-vision.webp"
            alt="Tangig"
            width="1920"
            height="1080"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>

        {/* Voile de lisibilité en haut, pour la navbar transparente */}
        <div className="tg-p1-topscrim" />

        {/* Voile de lisibilité en bas */}
        <div className="tg-p1-scrim" />

        {/* Texte posé sur l'image, à gauche */}
        <div className="tg-p1-overlay">
          <motion.h1
            className="tg-p1-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: appleEase, delay: 0.1 }}
          >
            Des systèmes.<br />
            <span className="tg-p1-title-accent">Pas des heures.</span>
          </motion.h1>

          <motion.p
            className="tg-p1-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: appleEase, delay: 0.2 }}
          >
            Des systèmes complets, prêts à installer, qui font avancer votre activité.
          </motion.p>
        </div>

        {/* CTAs, ancrés en bas à droite */}
        <motion.div
          className="tg-p1-links"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: appleEase, delay: 0.3 }}
        >
          <Link to="/marketplace" className="tg-p1-btn-ghost">
            Les Systèmes
          </Link>
          <Link to="/devenir-createur" className="tg-p1-btn-black">Demander l'accès</Link>
        </motion.div>

      </div>
    </>
  );
}
